// ===== 完了ダイアログ(品質測定) =====
// 全工程を完了にし、各工程の該当品質測定値を記録してから施工実施を完了(finished_at を記録)する。
// 必須(judge_required)項目が未入力なら確定不可。既存レコードは値が変わっていれば更新する。

import { useState, useMemo, useRef } from "react";
import { Stack, Typography, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { PageContext } from "../PageContext";
import { ExtendedDB } from "../../../types/extendedDB";
import type { ProcessExecutionBase } from "../../../schema/generated/ProcessExecution";
import type { QualityMeasurementItemBase } from "../../../schema/generated/QualityMeasurementItem";
import { areaLabel, workDetailName } from "./helpers";
import { errorMessageOf } from "../../../util/typed";

export function CompletionDialog({
  pageContext, db, constructionId, consumedMaterialIds, envPatch, onClose, onCompleted, reload, setSnackbar,
}: {
  pageContext: PageContext; db: ExtendedDB; constructionId: string;
  consumedMaterialIds: Set<number>;
  envPatch: { construction_plan_id: string; executed_at: number | null; ambient_temperature: number | null; ambient_humidity: number | null; remarks: string | null; has_stir_execution: boolean | null };
  onClose: () => void; onCompleted: () => void;
  reload: () => Promise<unknown> | void; setSnackbar: (s: string) => void;
}) {
  const processes = useMemo(
    () => db.processExecutions.filter((p) => p.construction_id === constructionId && p.deleted_at == null),
    [db.processExecutions, constructionId]
  );
  const existingRecords = useMemo(
    () => db.qualityRecords.filter((q) => q.construction_id === constructionId && q.deleted_at == null),
    [db.qualityRecords, constructionId]
  );
  const [qvalues, setQvalues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  // 工程実施に適用される品質項目(scope_kind 対応)。
  //   work_detail   : 工程一致
  //   work_subtype  : 施工箇所の種別一致
  //   work_type     : 施工箇所の工種一致
  //   material      : この施工の消費材料に一致(materialQrs 未同期環境では判定不可=非適用)
  //   construction  : 常に適用
  //   その他(旧データ): work_detail_id 一致 または 未設定なら全体適用
  const itemApplies = (it: QualityMeasurementItemBase, pe: ProcessExecutionBase): boolean => {
    const area = db.constructionPlanAreas.find((a) => a.id === pe.construction_plan_area_id);
    if (it.scope_kind === "work_detail") {
      return it.work_detail_id === pe.work_detail_id;
    }
    if (it.scope_kind === "work_subtype") {
      return area != null && it.work_subtype_id != null && it.work_subtype_id === area.work_subtype_id;
    }
    if (it.scope_kind === "work_type") {
      return area != null && it.work_type_id != null && it.work_type_id === area.work_type_id;
    }
    if (it.scope_kind === "material") {
      return it.material_id != null && consumedMaterialIds.has(it.material_id);
    }
    if (it.scope_kind === "construction") {
      return true;
    }
    if (it.work_detail_id != null) {
      return it.work_detail_id === pe.work_detail_id;
    }
    return true;
  };
  const itemsFor = (pe: ProcessExecutionBase) =>
    db.qualityMeasurementItems.filter((it) => it.deleted_at == null && itemApplies(it, pe));

  const keyOf = (pid: string, itemId: number) => `${pid}::${itemId}`;
  const existingRecordOf = (areaId: string, itemId: number) =>
    existingRecords.find((r) => r.construction_plan_area_id === areaId && r.quality_measurement_item_id === itemId) ?? null;
  const valueOf = (process: ProcessExecutionBase, item: QualityMeasurementItemBase): string => {
    const k = keyOf(process.id, item.id);
    const entered = qvalues[k];
    if (entered !== undefined) {
      return entered;
    }
    const rec = existingRecordOf(process.construction_plan_area_id, item.id);
    if (rec != null && rec.value != null) {
      return String(rec.value);
    }
    return "";
  };
  const computeJudgment = (v: number, item: QualityMeasurementItemBase): string => {
    if (item.lower_limit == null && item.upper_limit == null) {
      return "";
    }
    if (item.lower_limit != null && v < item.lower_limit) {
      return "NG";
    }
    if (item.upper_limit != null && v > item.upper_limit) {
      return "NG";
    }
    return "OK";
  };

  const missingRequired = processes.some((p) =>
    itemsFor(p).some((it) => it.judge_required && valueOf(p, it) === "")
  );

  const confirm = async () => {
    if (lock.current) return;
    if (missingRequired) {
      pageContext.api.showError("必須(★)の品質測定が未入力です。");
      return;
    }
    lock.current = true;
    setBusy(true);
    try {
      const writtenKeys = new Set<string>();
      for (const p of processes) {
        if (p.completed_at == null) {
          await pageContext.api.entities.patchRow("processExecutions", {
            id: p.id,
            construction_id: p.construction_id,
            construction_plan_area_id: p.construction_plan_area_id,
            work_detail_id: p.work_detail_id,
            completed_at: Date.now(),
            remarks: p.remarks,
          });
        }
        for (const it of itemsFor(p)) {
          const vs = valueOf(p, it);
          if (vs === "") {
            continue;
          }
          const areaItemKey = `${p.construction_plan_area_id}::${it.id}`;
          if (writtenKeys.has(areaItemKey)) {
            continue;
          }
          const v = Number(vs);
          if (Number.isNaN(v)) {
            pageContext.api.showError(`品質測定値が数値ではありません: ${it.name}`);
            return;
          }
          writtenKeys.add(areaItemKey);
          const existing = existingRecordOf(p.construction_plan_area_id, it.id);
          if (existing == null) {
            await pageContext.api.entities.putRow("qualityRecords", {
              construction_id: constructionId,
              construction_plan_area_id: p.construction_plan_area_id,
              quality_measurement_item_id: it.id,
              value: v,
              judgment: computeJudgment(v, it),
              construction_action_id: null,
              measured_at: Date.now(),
            });
          } else if (existing.value !== v) {
            await pageContext.api.entities.patchRow("qualityRecords", {
              id: existing.id,
              construction_id: existing.construction_id,
              construction_plan_area_id: existing.construction_plan_area_id,
              quality_measurement_item_id: existing.quality_measurement_item_id,
              value: v,
              judgment: computeJudgment(v, it),
              construction_action_id: existing.construction_action_id,
              measured_at: Date.now(),
            });
          }
        }
      }
      // 完了保存 = finished_at に完了時刻を記録する(下書き判定は finished_at の null 判定に一本化)。
      await pageContext.api.construction.patch({ id: constructionId, ...envPatch, finished_at: Date.now() });
      setSnackbar("施工実施を完了しました");
      await reload();
      onCompleted();
    } catch (e) {
      pageContext.api.showError(`完了に失敗しました: ${errorMessageOf(e)}`);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>完了して保存(品質測定)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          各工程の品質測定値を入力してください(★=必須)。確定すると全工程を完了にし、施工実施を完了(下書き解除)します。
        </Typography>
        <Stack gap={2}>
          {processes.map((p) => {
            const items = itemsFor(p);
            return (
              <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>{areaLabel(db, p.construction_plan_area_id)} / {workDetailName(db, p.work_detail_id)}</Typography>
                {items.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">該当する品質測定項目はありません</Typography>
                ) : (
                  <Stack gap={1}>
                    {items.map((it) => (
                      <Stack key={it.id} direction="row" gap={1} alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 220 }} color={it.judge_required ? "error" : "text.primary"}>
                          {it.judge_required ? "★ " : ""}{it.name}
                        </Typography>
                        <TextField size="small" type="number" value={valueOf(p, it)}
                          onChange={(e) => setQvalues((prev) => ({ ...prev, [keyOf(p.id, it.id)]: e.target.value }))}
                          InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">{it.unit}</Typography> }} sx={{ width: 160 }} />
                        <Typography variant="caption" color="text.secondary">
                          {it.lower_limit != null || it.upper_limit != null ? `規格 ${it.lower_limit ?? ""}〜${it.upper_limit ?? ""}` : ""}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>キャンセル</Button>
        <Button variant="contained" color="success" disabled={busy || missingRequired} onClick={confirm}>完了して保存</Button>
      </DialogActions>
    </Dialog>
  );
}
